import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState, memo } from 'react'
import type { Technology } from '@/types/entities'
import { buildIconPath } from '@/lib/imagePaths'

interface TechnologyActionsProps {
    technology: Technology
    onEdit: (technology: Technology) => void
    onDelete: (technology: Technology) => void
    onView: (technology: Technology) => void
}

const TechnologyActions = memo(function TechnologyActions({ technology, onEdit, onDelete, onView }: TechnologyActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('technologies.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView(technology)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('technologies.viewTechnology')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(technology)
                            setOpen(false)
                        }}
                    >
                        <Edit className="mr-2 h-4 w-4" />
                        {t('technologies.editTechnology')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(technology)
                            setOpen(false)
                        }}
                        className="text-destructive focus:text-destructive"
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('technologies.deleteTechnology')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
})

export function createTechnologyColumns(
    onEdit: (technology: Technology) => void,
    onDelete: (technology: Technology) => void,
    onView: (technology: Technology) => void,
    t: (key: string) => string
): ColumnDef<Technology>[] {
    return [
        {
            accessorKey: 'manufacturer',
            header: t('technologies.manufacturer'),
            size: 200,
            minSize: 150,
            maxSize: 300,
            cell: ({ row }) => {
                const name = row.getValue('manufacturer') as string
                const iconFilename = row.original.manufacturerIconFilename
                return (
                    <div className="flex items-center gap-2">
                        {iconFilename && (
                            <img src={buildIconPath(iconFilename)} alt="" className="h-5 w-5 object-contain flex-shrink-0" />
                        )}
                        <span>{name}</span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'category',
            header: t('technologies.category'),
            size: 200,
            minSize: 150,
            maxSize: 300,
        },
        {
            accessorKey: 'model',
            header: t('technologies.model'),
            size: 250,
            minSize: 200,
            maxSize: 400,
        },
        {
            accessorKey: 'description',
            header: t('technologies.description'),
            size: 400,
            minSize: 250,
            maxSize: 600,
            cell: ({ row }) => {
                const description = row.getValue('description') as string | null
                return (
                    <div className="text-sm">
                        {description || '-'}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: 80,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const technology = row.original
                return (
                    <TechnologyActions
                        technology={technology}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                    />
                )
            },
        },
    ]
}
